import {
  ATTRIBUTION_PARAMS,
  ATTRIBUTION_VALUE_MAX_LENGTH,
} from "@medbillai/schemas/attribution";

import type { AttributionTraits } from "./types";

/**
 * Allow-list of a query string: keeps the attribution parameters, drops
 * everything else. Query strings also carry credentials — `/authenticate`
 * arrives with a live magic-link or OTP token — so anything sent onwards to an
 * analytics destination has to be picked, never forwarded wholesale.
 *
 * @param queryParams - Query parameters from URL (e.g., from Next.js router.query)
 */
export function pickAttributionParams(
  queryParams: Record<string, string | string[] | undefined>,
): Record<string, string> {
  const picked: Record<string, string> = {};

  for (const param of ATTRIBUTION_PARAMS) {
    const value = queryParams[param];
    if (value && typeof value === "string") {
      picked[param] = value.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
    }
  }

  return picked;
}

/**
 * Extracts attribution parameters from URL query parameters.
 * Only includes parameters that are present in the query string.
 *
 * @param queryParams - Query parameters from URL (e.g., from Next.js router.query)
 * @returns Attribution traits object with only the parameters that were present
 */
export function extractAttributionTraits(
  queryParams: Record<string, string | string[] | undefined>,
): AttributionTraits {
  const traits: AttributionTraits = pickAttributionParams(queryParams);

  // Add timestamp if we found any attribution parameters
  if (Object.keys(traits).length > 0) {
    traits.attribution_timestamp = new Date().toISOString();
  }

  return traits;
}

/**
 * Checks if the query parameters contain any attribution data.
 *
 * @param queryParams - Query parameters from URL
 * @returns true if any attribution parameters are present
 */
export function hasAttributionParams(
  queryParams: Record<string, string | string[] | undefined>,
): boolean {
  return ATTRIBUTION_PARAMS.some(param => {
    const value = queryParams[param];
    return value && typeof value === "string";
  });
}
